/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { Logger } from "@azure/msal-browser";
import { IExtendedTelemetryItem } from "@microsoft/1ds-analytics-js";
import {
    PerformanceCallbackFunction,
    PerformanceEvent,
    PerformanceEvents,
} from "./TelemetryUtils.js";
import { MATSTelemetryReporter } from "./MATSTelemetryReporter.js";
import { PerformanceTelemetryReporter } from "./PerformanceTelemetryReporter.js";

export type PerformanceTelemetryEvent = IExtendedTelemetryItem["data"] & {
    name: string;
};
export type TelemetryReporterConfiguration = {
    disabled?: boolean;
    /**
     * @deprecated all events are sampled
     */
    samplingPercentage?: number;
    eventsToEmit?: PerformanceEvents[];
    enableMATS?: boolean;
    appName?: string;
    appVersion?: string;
    synthetic?: boolean;
};

const allowedEvents: Set<PerformanceEvents> = new Set([
    PerformanceEvents.AcquireTokenSilent,
    PerformanceEvents.AcquireTokenByCode,
    PerformanceEvents.AcquireTokenPopup,
    PerformanceEvents.AcquireTokenRedirect,
    PerformanceEvents.AcquireTokenPreRedirect,
    PerformanceEvents.PairwiseBrokerApplicationInitializeBrokering,
    PerformanceEvents.SsoSilent,
    // Embedded events
    PerformanceEvents.EmbeddedClientApplicationSendSSOSilentRequest,
    PerformanceEvents.EmbeddedClientApplicationSendPopupRequest,
    PerformanceEvents.EmbeddedClientApplicationSendRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationHandleRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationSendSilentRefreshRequest,
]);

export class TelemetryReporter {
    private matsTelemetryReporter: MATSTelemetryReporter | null;
    private perfTelemetryReporter: PerformanceTelemetryReporter;
    private logger: Logger;

    constructor(
        logger: Logger,
        configuration?: TelemetryReporterConfiguration
    ) {
        this.logger = logger;

        const eventsToEmit: PerformanceEvents[] =
            configuration?.eventsToEmit?.filter((event) =>
                allowedEvents?.has(event)
            ) || [...allowedEvents];

        this.matsTelemetryReporter = configuration?.enableMATS
            ? new MATSTelemetryReporter(
                  logger,
                  eventsToEmit,
                  configuration?.appName,
                  configuration?.appVersion
              )
            : null;

        this.perfTelemetryReporter = new PerformanceTelemetryReporter(
            logger,
            eventsToEmit,
            configuration?.synthetic
        );
    }

    private sendCustomEvent(event: PerformanceEvent): void {
        this.perfTelemetryReporter.sendCustomEvent(event);
        this.matsTelemetryReporter?.sendCustomEvent(event);
    }

    getTelemetryCallback(): PerformanceCallbackFunction {
        this.logger.trace(
            "TelemetryReporter: Attaching callback to send custom metrics."
        );
        const telemetryCallback: PerformanceCallbackFunction = (
            events: PerformanceEvent[]
        ) => {
            events.forEach((event) => {
                /*
                 * Set proper event name for broker event such that instead of grouping all broker events under the same table
                 * they are placed in the corresponding tables based on the underlying API names
                 * e.g. acquireTokenByBroker -> (acquireTokenSilent | ssoSilent | acquireTokenRedirect | acquireTokenPopup)
                 */
                if (event.name === PerformanceEvents.AcquireTokenByBroker) {
                    event.name = event.brokerEventName || event.name;
                    event.isBroker = true;
                    delete event.brokerEventName;
                    this.logger.verbosePii(
                        `TelemetryReporter: Updated broker event ${JSON.stringify(
                            event
                        )}`,
                        event.correlationId
                    );
                }
                this.sendCustomEvent(event);
            });
        };
        this.logger.verbose(
            "TelemetryReporter: Returning callback as telemetry emitted."
        );
        return telemetryCallback;
    }
}
